import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { api } from '../services/api';
import { Todo, ReviewEntry } from '../types';

export interface Day {
  id: string;
  user_id: string;
  year: number;
  month: number;
  day: number;
  date: string;
  created_time: string;
  updated_time: string;
  is_review_completed: boolean;
  review_completed_time?: string;
  
  // Start/End of day workflow tracking
  is_day_started: boolean;
  day_started_time?: string;
  is_day_ended: boolean;
  day_ended_time?: string;
  
  // Mood and Energy tracking
  morning_mood?: string;
  morning_energy?: string;
  evening_mood?: string;
  evening_energy?: string;
  
  // Original journaling fields
  goals: string[];
  reflections: Record<string, string>;
  gratitude: string[];
  accomplishments: string[];
  challenges: string[];
  learnings: string[];
  
  // Enhanced journaling fields
  start_day_reflections: ReviewEntry[];
  priorities_for_day: string[];
  end_day_reflections: ReviewEntry[];
  went_well: string[];
  could_improve: string[];
  remember_tomorrow: string[];
  general_reflections: ReviewEntry[];
  refresh_notes?: string;
  mood_logs: Array<{timestamp: string; mood?: string; energy?: string}>;

  // Task associations
  active_tasks: string[];

  // Configuration
  review_prompts: Record<string, string>;
  visible_fields: Record<string, unknown>;
  review_notes?: string;
}

export interface DayTasksResponse {
  day: Day;
  completed_tasks: Todo[];
}

export interface DayUpdate {
  // Start/End of day workflow tracking
  is_day_started?: boolean;
  day_started_time?: string;
  is_day_ended?: boolean;
  day_ended_time?: string;
  
  // Mood and Energy tracking
  morning_mood?: string;
  morning_energy?: string;
  evening_mood?: string;
  evening_energy?: string;
  
  // Original journaling fields
  goals?: string[];
  reflections?: Record<string, string>;
  gratitude?: string[];
  accomplishments?: string[];
  challenges?: string[];
  learnings?: string[];
  
  // Enhanced journaling fields
  start_day_reflections?: ReviewEntry[];
  priorities_for_day?: string[];
  end_day_reflections?: ReviewEntry[];
  went_well?: string[];
  could_improve?: string[];
  remember_tomorrow?: string[];
  general_reflections?: ReviewEntry[];
  refresh_notes?: string;
  mood_logs?: Array<{timestamp: string; mood?: string; energy?: string}>;
  current_mood?: string;
  current_energy?: string;

  // Task associations
  active_tasks?: string[];

  // Configuration
  review_prompts?: Record<string, string>;
  visible_fields?: Record<string, unknown>;
  review_notes?: string;
  is_review_completed?: boolean;
}

export const useDays = (selectedDate: Date) => {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  // Get today's day data
  const { data: todayData, isLoading: isTodayLoading } = useQuery({
    queryKey: ['days', 'today'],
    queryFn: async (): Promise<DayTasksResponse> => {
      const response = await api.get('/days/today');
      return response.data;
    },
    enabled: selectedDate.toDateString() === new Date().toDateString(),
  });

  // Get specific day data for historical dates
  const { data: dayData, isLoading: isDayLoading } = useQuery({
    queryKey: ['days', format(selectedDate, 'yyyy-MM-dd')],
    queryFn: async (): Promise<DayTasksResponse | null> => {
      try {
        // First try to get existing day data
        const response = await api.get(`/days/?year=${selectedDate.getFullYear()}&month=${selectedDate.getMonth() + 1}&limit=1`);
        const days = response.data;
        
        const dayMatch = days.find((d: Day) => 
          d.year === selectedDate.getFullYear() && 
          d.month === selectedDate.getMonth() + 1 && 
          d.day === selectedDate.getDate()
        );

        if (dayMatch) {
          // Get completed tasks for this day
          const tasksResponse = await api.get(`/days/${dayMatch.id}`);
          return tasksResponse.data;
        }
        
        return null;
      } catch (err) {
        console.error('Error fetching day data:', err);
        return null;
      }
    },
    enabled: selectedDate.toDateString() !== new Date().toDateString(),
  });

  // Update day mutation
  const updateDayMutation = useMutation({
    mutationFn: async ({ dayId, data }: { dayId: string; data: DayUpdate }) => {
      const response = await api.patch(`/days/${dayId}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['days'] });
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr.response?.data?.detail || 'Failed to update day');
    },
  });

  // Start day
  const startDay = async (dayId: string, startData: Record<string, unknown>) => {
    try {
      const response = await api.post(`/days/${dayId}/start`, startData);
      queryClient.invalidateQueries({ queryKey: ['days'] });
      return response.data;
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      throw new Error(axiosErr.response?.data?.detail || 'Failed to start day');
    }
  };

  // End day
  const endDay = async (dayId: string, endData: Record<string, unknown>) => {
    try {
      const response = await api.post(`/days/${dayId}/end`, endData);
      queryClient.invalidateQueries({ queryKey: ['days'] });
      return response.data;
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      throw new Error(axiosErr.response?.data?.detail || 'Failed to end day');
    }
  };

  // Add tasks to day
  const addTasksToDay = async (dayId: string, taskIds: string[]) => {
    try {
      const response = await api.post(`/days/${dayId}/add-tasks`, { task_ids: taskIds });
      queryClient.invalidateQueries({ queryKey: ['days'] });
      queryClient.invalidateQueries({ queryKey: ['todos'] });
      return response.data;
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      throw new Error(axiosErr.response?.data?.detail || 'Failed to add tasks to day');
    }
  };

  // Update day function
  const updateDay = async (dayId: string, data: DayUpdate) => {
    return updateDayMutation.mutateAsync({ dayId, data });
  };

  // Determine which data to use based on selected date
  const isToday = selectedDate.toDateString() === new Date().toDateString();
  const currentData = isToday ? todayData : dayData;
  const isLoading = isToday ? isTodayLoading : isDayLoading;

  return {
    currentDay: currentData?.day || null,
    completedTasks: currentData?.completed_tasks || [],
    updateDay,
    startDay,
    endDay,
    addTasksToDay,
    isLoading,
    error,
    isUpdating: updateDayMutation.isPending,
  };
};